"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.computeBbox = computeBbox;
exports.computeBbox_3 = computeBbox_3;
var _vector = require("./vector");
var _getPointTangent = require("./getPointTangent");
var _convertLineToArc = require("./convertLineToArc");
/**
 * Computes the bounding box of an array of entities
 * @param entities An array of entities
 * @returns The bounding box of an array of entities
 */
function computeBbox(entities) {
  let bbox = {};
  entities.forEach(e => {
    switch (e.type) {
      case 'ARC':
        extendBbox(bbox, (0, _getPointTangent.getPointTangentOfEntity)(e, 0)[0]);
        extendBbox(bbox, (0, _getPointTangent.getPointTangentOfEntity)(e, 1)[0]);
        let sa = e.startAngle;
        let ea = e.endAngle;
        while (sa < 0) {
          sa += Math.PI * 2;
          ea += Math.PI * 2;
        }
        while (sa >= Math.PI * 2) {
          sa -= Math.PI * 2;
          ea -= Math.PI * 2;
        }
        while (ea - sa <= 0) {
          ea += Math.PI * 2;
        }
        while (ea - sa > Math.PI * 2) {
          ea -= Math.PI * 2;
        }
        for (let a = 0; a <= ea; a += Math.PI / 2) {
          if (a >= sa) {
            extendBbox(bbox, {
              x: e.x + e.r * Math.cos(a),
              y: e.y + e.r * Math.sin(a)
            });
          }
        }
        break;
      case 'LINE':
        extendBbox(bbox, e.start);
        extendBbox(bbox, e.end);
        break;
      case 'CIRCLE':
        extendBbox(bbox, {
          x: e.x + e.r,
          y: e.y + e.r
        });
        extendBbox(bbox, {
          x: e.x - e.r,
          y: e.y - e.r
        });
        break;
      case 'LWPOLYLINE':
      case 'POLYLINE':
        const verts = e.vertices.slice();
        if (e.closed) {
          verts.push(verts[0]);
        }
        for (let i = 0; i < verts.length; i++) {
          let ip1 = (i + 1) % verts.length;
          extendBbox(bbox, verts[i]);
          let bulge = verts[i].bulge;
          if (bulge && (bulge < 0 || bulge > 0) && i < verts.length - 1) {
            try {
              let arc = (0, _convertLineToArc.convertLineToArc)(verts[i], verts[ip1], bulge);
              let arcbbox = computeBbox([arc]);
              extendBbox(bbox, arcbbox.min);
              extendBbox(bbox, arcbbox.max);
            } catch (ex) {
              console.log(ex);
            }
          }
        }
        break;
      default:
        throw new Error('Unsupported entity: ' + e.type);
    }
  });
  return bbox;
}

/**
 * Computes the 3d bounding box of an array of entities which have been transformed by a matrix.
 * @param entities An array of entities
 * @param matrix A matrix to transform the entities by
 * @param zHeight The z height to set on each entity before applying the transform matrix. Default is 0.
 * @returns The bounding box of an array of entities
 */
function computeBbox_3(entities, matrix, zHeight = 0) {
  let bbox = {};
  entities.forEach(e => {
    switch (e.type) {
      case 'ARC':
        {
          /**
           * Project the rotated circle onto a plane and examine one direction at a time. For example, the y-direction.
           * Determine what direction each end is pointing. Then:
           * 
           * Case 1: Ends pointing in the same direction, then you know there is 1 maximum (or minimum) in y between the
           * ends.
           * 
           * Case 2: Ends opposite directions pointing apart: a short arc, so 0 maximums or minimums in y between the
           * ends.
           *
           * Case 3: Ends opposite directions pointing towards each other: 1 maximum and 1 minimum in y.
           * 
           * Wherever there is a maximum or minimum, its value is the same as that of the circle. If there is no maximum
           * or minimum, then just use the maximum or minimum of the endpoints themselves.
           * 
           * Repeat for all directions x, y, and z.
           */

          let circleBbox = getCircleBbox(e, matrix, zHeight);

          // If the arc is a full circle, just extend the bbox to the full circle's bbox.
          if (e.endAngle - e.startAngle >= Math.PI * 2 - 1e-12) {
            extendBbox_3(bbox, circleBbox.min);
            extendBbox_3(bbox, circleBbox.max);
            break;
          }

          // Get endpoints of the arc
          let [startPoint, startTangent] = (0, _getPointTangent.getPointTangentOfEntity)(e, 0);
          let [endPoint, endTangent] = (0, _getPointTangent.getPointTangentOfEntity)(e, 1);

          // If the arc is just a very tiny segment, just extend the bbox to the start and end points.
          if (Math.abs(e.endAngle - e.startAngle) < 1e-12) {
            extendBbox_3(bbox, (0, _vector.multiplyMatrix4Point)(matrix, {
              ...startPoint,
              z: zHeight
            }));
            extendBbox_3(bbox, (0, _vector.multiplyMatrix4Point)(matrix, {
              ...endPoint,
              z: zHeight
            }));
            break;
          }

          // Transform the endpoints and tangents
          let startPoint3 = (0, _vector.multiplyMatrix4Point)(matrix, {
            ...startPoint,
            z: zHeight
          });
          let endPoint3 = (0, _vector.multiplyMatrix4Point)(matrix, {
            ...endPoint,
            z: zHeight
          });
          let startTangent3 = (0, _vector.multiplyMatrix3Point)(matrix, {
            ...startTangent,
            z: 0
          });
          let endTangent3 = (0, _vector.multiplyMatrix3Point)(matrix, {
            ...endTangent,
            z: 0
          });

          // Reverse starting tangent so it points away from the arc for clarity
          startTangent3 = (0, _vector.mul_3)(startTangent3, -1);

          // Initialize the bbox to the endpoints
          extendBbox_3(bbox, startPoint3);
          extendBbox_3(bbox, endPoint3);
          for (let dim of ['x', 'y', 'z']) {
            // For each dimension, see if the tangents are pointing in the same or opposite directions, and add the
            // corresponding extremum of the full circle's bounding box accordingly.

            let unitNormal = {
              x: 0,
              y: 0,
              z: 0
            };
            unitNormal[dim] = 1;
            let startTangentDot = (0, _vector.dot_3)(startTangent3, unitNormal);
            let endTangentDot = (0, _vector.dot_3)(endTangent3, unitNormal);
            if (startTangentDot >= 0 && endTangentDot >= 0) {
              // Case 1: Ends pointing in the same direction (positive direction): 1 minimum between these points. Extend
              // the box to the full circle minimum in this direction.
              bbox.min[dim] = Math.min(bbox.min[dim], circleBbox.min[dim]);
            } else if (startTangentDot <= 0 && endTangentDot <= 0) {
              // Case 1: Ends pointing in the same direction (negative direction): 1 maximum between these points. Extend
              // the box to the full circle maximum in this direction.
              bbox.max[dim] = Math.max(bbox.max[dim], circleBbox.max[dim]);
            } else if (startTangentDot <= 0 && endTangentDot >= 0 && startPoint3[dim] > endPoint3[dim] || startTangentDot >= 0 && endTangentDot <= 0 && startPoint3[dim] < endPoint3[dim]) {
              // Case 3: Ends pointing towards each other: 1 maximum and 1 minimum in this direction. Extend the box to
              // the full circle maximum and minimum in this direction.
              bbox.min[dim] = Math.min(bbox.min[dim], circleBbox.min[dim]);
              bbox.max[dim] = Math.max(bbox.max[dim], circleBbox.max[dim]);
            } else {
              // Case 2: Ends pointing apart: 0 maximums or minimums in this direction. Don't extend the box any more.
            }
          }
          break;
        }
      case 'LINE':
        {
          extendBbox_3(bbox, (0, _vector.multiplyMatrix4Point)(matrix, {
            ...e.start,
            z: zHeight
          }));
          extendBbox_3(bbox, (0, _vector.multiplyMatrix4Point)(matrix, {
            ...e.end,
            z: zHeight
          }));
          break;
        }
      case 'CIRCLE':
        {
          let circleBbox = getCircleBbox(e, matrix, zHeight);
          extendBbox_3(bbox, circleBbox.min);
          extendBbox_3(bbox, circleBbox.max);
          break;
        }
      case 'LWPOLYLINE':
      case 'POLYLINE':
        {
          const verts = e.vertices.slice();
          if (e.closed) {
            verts.push(verts[0]);
          }
          for (let i = 0; i < verts.length; i++) {
            let ip1 = (i + 1) % verts.length;
            extendBbox_3(bbox, (0, _vector.multiplyMatrix4Point)(matrix, {
              ...verts[i],
              z: zHeight
            }));
            let bulge = verts[i].bulge;
            if (bulge && (bulge < 0 || bulge > 0) && i < verts.length - 1) {
              try {
                let arc = (0, _convertLineToArc.convertLineToArc)(verts[i], verts[ip1], bulge);
                let arcbbox = computeBbox_3([arc], matrix, zHeight);
                extendBbox_3(bbox, arcbbox.min);
                extendBbox_3(bbox, arcbbox.max);
              } catch (ex) {
                console.log(ex);
              }
            }
          }
          break;
        }
      default:
        throw new Error('Unsupported entity: ' + e.type);
    }
  });
  return bbox;
}
function getCircleBbox(e, matrix, zHeight) {
  if (e.type !== 'CIRCLE' && e.type !== 'ARC') {
    throw new Error('Entity is not a circle or arc');
  }
  let circleAxisTrans = (0, _vector.multiplyMatrix3Point)(matrix, {
    x: 0,
    y: 0,
    z: 1
  });
  let circleCenter = (0, _vector.multiplyMatrix4Point)(matrix, {
    x: e.x,
    y: e.y,
    z: zHeight
  });
  let xCross = (0, _vector.norm_3)((0, _vector.cross_3)(circleAxisTrans, {
    x: 1,
    y: 0,
    z: 0
  }));
  let yCross = (0, _vector.norm_3)((0, _vector.cross_3)(circleAxisTrans, {
    x: 0,
    y: 1,
    z: 0
  }));
  let zCross = (0, _vector.norm_3)((0, _vector.cross_3)(circleAxisTrans, {
    x: 0,
    y: 0,
    z: 1
  }));
  let centerToCorner = {
    x: e.r * xCross,
    y: e.r * yCross,
    z: e.r * zCross
  };
  return {
    min: (0, _vector.sub_3)(circleCenter, centerToCorner),
    max: (0, _vector.add_3)(circleCenter, centerToCorner)
  };
}
function extendBbox(bbox, p) {
  bbox.min = (0, _vector.min)(bbox.min, p);
  bbox.max = (0, _vector.max)(bbox.max, p);
}
function extendBbox_3(bbox, p) {
  bbox.min = (0, _vector.min_3)(bbox.min, p);
  bbox.max = (0, _vector.max_3)(bbox.max, p);
}